namespace Solovis.UI {

    export interface TabStripControlOptions extends Solovis.UI.BaseControlOptions  {
        tabs: TabStripControlTabOptions[];

        tabContextMenuItemProvider?: () => PopupLinkPanelLink[];
    }

    export interface TabStripControlTabModel {
        tabCode: string;
        tabContainerControl?: any;
        tabDiv?: any;
        tabContentDiv?: any;        
        tabOptions: TabStripControlTabOptions;
        custom: any;
    }

    export interface TabStripControlTabOptions {
        code: string;
        label: string;      
        content?: any;
        noPadding?: boolean;
        custom?: any;

        init?: () => void;
        activate?: () => void;
        deactivate?: () => void;
        destroy?: () => void;
    }

    /*
     * TabStripControl is a UI element for a set of tabs.
     * 
     * options:
     *  tabs: array of |tab options|
     * 
     * |tab options|
     *  code: string to uniquely identify the tab.
     *  label: label for the tab.
     *  init: called when tab is first created
     *  activate: called when tab is activated
     *  deactivate: called when tab is deactivated
     *  destroy: called when tab is destroyed
     * 
     *  init/activate/deactivate/destroy are called with "tabModel" as the "this" context which
     *  has tabContainerControl, tabContentDiv, tabCode, and tabOptions. Consumers of this class
     *  should store any custom data on tabModel.custom to avoid naming conflicts with future properties
     *  introduced by the TabStripControl.
     */
    export class TabStripControl extends Solovis.UI.BaseControl {

        public _initialTabOptions: TabStripControlTabOptions[];
        public _tabModelList: TabStripControlTabModel[];
        public _activeTabModel: TabStripControlTabModel;
        public _tabStripContainer: JQuery;
        public _tabList: JQuery;
        public _tabStrip: any;

        private _tabContextMenuItemProvider: () => PopupLinkPanelLink[];

        constructor(options: TabStripControlOptions) {
            super(options);

            this._initialTabOptions = options.tabs ? options.tabs : null;

            this._tabModelList = null;

            this._tabContextMenuItemProvider = options.tabContextMenuItemProvider;
        }

        public doRender(): void {
            var self = this;

            this._tabModelList = [];
            this._activeTabModel = null;

            // Setup tab container.
            this._tabStripContainer = $("<div>").appendTo(this.container);
            this._tabList = $("<ul>").appendTo(this._tabStripContainer);

            // Initialize the tabstrip.
            this._tabStrip = this._tabStripContainer.kendoTabStrip({
                animation: false,

                activate: function (e) {
                    self._handleTabActivate((e.item as any).__tabModel);
                }
            });

            for (let tabOptions of this._initialTabOptions) {            
                this.insertTab(tabOptions, -1);
            }

            if (this._tabContextMenuItemProvider) {
                $(".k-tabstrip-items", this._tabStripContainer).on("contextmenu", (e) => {                    
                    let links = this._tabContextMenuItemProvider();
                    if (links) {
                        e.preventDefault();

                        Solovis.UI.PopupLinkPanel.showLinks({
                            links: links,
                            offset: e
                        });

                        return false;
                    }
                });
            }
        }


        public insertTab(tabOptions: TabStripControlTabOptions, newTabIndex: number): void {
            var self = this;

            var tabWillBeLast = false;

            var tabModelIndex;
            if (typeof newTabIndex === "undefined") {
                newTabIndex = null;
            }
            if (Solovis.isNotNull(newTabIndex) && newTabIndex >= 0) {
                tabModelIndex = newTabIndex;
            }
            else {
                tabModelIndex = this._tabModelList.length + (newTabIndex + 1);
            }
            if (tabModelIndex < 0) {
                tabModelIndex = 0;
            }
            else if (tabModelIndex >= this._tabModelList.length) {
                tabModelIndex = this._tabModelList.length;
                tabWillBeLast = true;
            }

            var tabCode = tabOptions.code ? tabOptions.code : null;
            var tabLabel = tabOptions.label ? tabOptions.label : "Tab";

            var tabModel: TabStripControlTabModel = {
                tabContainerControl: self,

                tabCode: tabCode,
                tabOptions: tabOptions,

                custom: {}
            };

            var tabStripObj = this._tabStrip.data("kendoTabStrip");

            if (tabWillBeLast) {
                this._tabModelList.push(tabModel);
                tabStripObj.append({
                    text: tabLabel,
                    content: " "
                });
            } else {
                this._tabModelList.splice(tabModelIndex, 0, tabModel);
                tabStripObj.insertBefore(
                    {
                        text: tabLabel,
                        content: " "
                    },
                    tabStripObj.tabGroup.children().eq(tabModelIndex));
            }

            // Get the tab div and set the model on it.
            var tabDiv = tabStripObj.tabGroup.children().eq(tabModelIndex);
            tabDiv.get(0).__tabModel = tabModel;

            tabModel.tabDiv = tabDiv;

            // Get the tab content div.
            var tabContentDiv = $(tabStripObj.contentElement(tabModelIndex)).html("");
            if (tabOptions.content) {
                tabContentDiv.append(tabOptions.content);
            }

            tabModel.tabContentDiv = tabContentDiv;
            tabModel.tabContentDiv.addClass("solovis-tab-content");
            if (tabOptions.noPadding) {
                tabModel.tabContentDiv.addClass("solovis-tab-content-no-padding");
            }

            // Call initialization method on tabModel if necessary.
            if (tabModel.tabOptions.init) {
                tabModel.tabOptions.init.call(tabModel);
            }
        }

        public removeTab(tabModelOrCodeOrIndex) {
            var tabModel = this._getTabModel(tabModelOrCodeOrIndex);

            if (tabModel && this._tabStrip) {
                var tabIndex = _.indexOf(this._tabModelList, tabModel);
                if (tabIndex >= 0) {
                    this._tabModelList.splice(tabIndex, 1);
                    this._tabStrip.data("kendoTabStrip").remove(tabModel.tabDiv);

                    if (tabModel.tabOptions.destroy) {
                        tabModel.tabOptions.destroy.call(tabModel);
                    }
                }
            }
        }

        public activateTab(tabModelOrCodeOrIndex) {
            var tabModel = this._getTabModel(tabModelOrCodeOrIndex);

            if (tabModel && this._tabStrip) {
                this._tabStrip.data("kendoTabStrip").activateTab(tabModel.tabDiv);
            }
        }

        public _getTabModel(tabModelOrCodeOrIndex): TabStripControlTabModel {
            var tabModel = null;

            if (typeof tabModelOrCodeOrIndex == "number") {
                var tabIndex = tabModelOrCodeOrIndex;
                if (this._tabModelList && tabIndex < this._tabModelList.length) {
                    tabModel = this._tabModelList[tabIndex];
                }
            }
            else if (typeof tabModelOrCodeOrIndex == "string") {
                var code = tabModelOrCodeOrIndex;
                tabModel = this.getTabModelByTabCode(code);
            }
            else if (typeof tabModelOrCodeOrIndex == "object") {
                tabModel = tabModelOrCodeOrIndex;
            }

            return tabModel;
        }

        public getTabIndexByTabCode(tabCode: string): number {
            /// <summary>
            /// Get index of the tab within model list given the tab code. Returns -1 if not found.
            /// <summary>
            var tabModel = _.find(this._tabModelList, function (t: any) { return t.tabCode == tabCode });
            if (tabModel) {
                var tabIndex = _.indexOf(this._tabModelList, tabModel);
                return tabIndex;
            }
            else {
                return -1;
            }
        }

        public getTabModelByTabCode(tabCode): TabStripControlTabModel {
            var tabModel = _.find(this._tabModelList, function (t: TabStripControlTabModel) { return t.tabCode == tabCode });
            return tabModel;
        }

        public getActiveTabModel(): TabStripControlTabModel {
            return this._activeTabModel;
        }

        public getTabModelList(): TabStripControlTabModel[] {
            if (this._tabModelList) {
                return this._tabModelList.slice(0);
            }
            else {
                return undefined;
            }
        }

        public getTabCount(): number {
            if (this._tabModelList) {
                return this._tabModelList.length;
            }
            else {
                return 0;
            }
        }

        public _handleTabActivate(tabModel: TabStripControlTabModel) {
            if (this._activeTabModel && tabModel !== this._activeTabModel) {
                // Handle deactivation of old active tab.
                this._handleTabDeactivate(this._activeTabModel);
            }

            this._activeTabModel = tabModel;

            if (tabModel) {
                var tabOptions = tabModel.tabOptions;

                if (tabOptions && tabOptions.activate) {
                    tabOptions.activate.call(tabModel);
                }
            }
        }

        public _handleTabDeactivate(tabModel: TabStripControlTabModel) {
            var tabOptions = tabModel.tabOptions;

            if (tabOptions && tabOptions.deactivate) {
                tabOptions.deactivate.call(tabModel);
            }
        }

        public getTabPickerHeight() {

            // Initialize with an approximation of the tab pickers' height
            var tabPickerHeight = 31;

            // Obtain an exact height, if possible
            var tabPicker = this._tabStripContainer.find("ul.k-tabstrip-items");
            if (tabPicker) {
                tabPickerHeight = tabPicker.outerHeight();
            }

            return tabPickerHeight;
        }

        public doDestroy() {
            for (let tabModel of this._tabModelList) {            
                if (tabModel.tabOptions.destroy) {
                    tabModel.tabOptions.destroy.call(tabModel);
                }
            }
        }
    }       

}